import React, { useState, useEffect } from "react";
import API from "../../services/api";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "../../components/Payments/CheckoutForm";
import Modal from "../../components/Facilities/Modal";
import InvoiceForm from "../../components/Invoices/InvoiceForm";
import { useAuth } from "../../context/AuthContext";
import { t } from "@lingui/macro";
import "./SummaryModal.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  { locale: "es" }
);

const DEFAULT_TEST_RECEIVER = {
  name: "Foreign Test Client Ltd",
  rfc: "XEXX010101000",
  cfdiUse: "S01",
  fiscalRegime: "616",
  taxZipCode: "76130",
};

const SummaryModal = ({ reservationData, onBack, onSuccess }) => {
  const { userRole } = useAuth();
  const { startDate, endDate, roomSelections, totalPrice, guestCount } =
    reservationData;

  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paidAmount, setPaidAmount] = useState(Number((totalPrice * 0.2).toFixed(2)));

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoiceReceiver, setInvoiceReceiver] = useState(DEFAULT_TEST_RECEIVER);

  const isEmailValid = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPhoneValid = (phone) =>
    /^[0-9]{9,15}$/.test(phone);
  const isFormValid = isEmailValid(email) && isPhoneValid(phoneNumber);

  const isFullPayment =
    Number(paidAmount.toFixed(2)) === Number(totalPrice.toFixed(2));

  useEffect(() => {
    if (!isFullPayment) {
      setWantsInvoice(false);
    }
  }, [isFullPayment]);

  // ===============================
  // WORKER / MANAGER – bez płatności
  // ===============================
  const handleCreateReservationWithoutPayment = async () => {
    const requestData = {
      email,
      phoneNumber,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      rooms: Object.entries(roomSelections)
        .filter(([_, amount]) => amount > 0)
        .map(([type, amount]) => ({ type, amount })),
      paidAmount: 0,
      totalAmount: Number(totalPrice.toFixed(2)),
      guestsCount: guestCount,
      invoiceRequired: wantsInvoice,
      invoiceReceiver: wantsInvoice ? invoiceReceiver : null,
      paymentId: null,
    };

    console.log("📤 Sending reservation (worker):", requestData);

    try {
      await API.post("/api/reservations/create", requestData);
      setSuccessMessage(t`Reservation created successfully!`);
      onSuccess();
    } catch (e) {
      console.error(e);
      setErrorMessage(t`Error creating reservation. Please try again.`);
    }
  };

  // ===============================
  // PUBLIC USER – Stripe Payment
  // ===============================
  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const response = await API.post(
          "/api/payments/public/create-payment-intent",
          {
            amount: Math.round(paidAmount * 100), // cents
          }
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error("Error fetching client secret:", error);
      }
    };

    if (showPaymentForm && !clientSecret) {
      fetchClientSecret();
    }
  }, [paidAmount, showPaymentForm, clientSecret]);

  const handleProceedToPayment = () => {
    if (!isFormValid) {
      setShowValidationErrors(true);
      return;
    }

    if (userRole === "ROLE_WORKER" || userRole === "ROLE_MANAGER") {
      handleCreateReservationWithoutPayment();
    } else {
      setShowPaymentForm(true);
    }
  };

  const handlePaymentSuccess = async (paymentId) => {
    const requestData = {
      email,
      phoneNumber,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      rooms: Object.entries(roomSelections)
        .filter(([_, amount]) => amount > 0)
        .map(([type, amount]) => ({ type, amount })),
      paidAmount: Number(paidAmount.toFixed(2)),      // informacyjne
      totalAmount: Number(totalPrice.toFixed(2)),     // kluczowe
      guestsCount: guestCount,
      invoiceRequired: wantsInvoice,
      invoiceReceiver: wantsInvoice ? invoiceReceiver : null,
      paymentId,                                      // PRAWDZIWA PŁATNOŚĆ
    };

    console.log("📤 Sending reservation (public):", requestData);

    try {
      const response = await API.post(
        "/api/reservations/public/create",
        requestData
      );

      setSuccessMessage(
        wantsInvoice
          ? t`Reservation created. Your invoice is being prepared.`
          : t`Reservation created successfully!`
      );
      onSuccess();
    } catch (e) {
      console.error(e);
      setErrorMessage(t`Error creating reservation. Please try again.`);
    }
  };

  const paymentData = {
    amount: paidAmount,
    currency: "mxn",
    country: "MX",
    label: t`Total`,
    payerName: email,
    payerEmail: email,
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t`Reservation Summary`}</h2>

        <div className="summary-details">
          <p><strong>{t`Check-in`}:</strong> {startDate?.toLocaleDateString()}</p>
          <p><strong>{t`Check-out`}:</strong> {endDate?.toLocaleDateString()}</p>
          <p><strong>{t`Guests`}:</strong> {guestCount}</p>

          <h3>{t`Total Price`}: {totalPrice.toFixed(2)} MXN</h3>
        </div>

        <div className="input-container">
          <label>{t`Email`}:</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className="input-container">
          <label>{t`Phone Number`}:</label>
          <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
        </div>

        {userRole !== "ROLE_WORKER" && userRole !== "ROLE_MANAGER" && (
          <div className="input-container">
            <label>{t`Deposit (min 20%)`}:</label>
            <input
              type="number"
              min={(totalPrice * 0.2).toFixed(2)}
              max={totalPrice.toFixed(2)}
              step="0.01"
              value={paidAmount}
              onChange={(e) => setPaidAmount(Number(e.target.value) || 0)}
            />
          </div>
        )}

        <div className="modal-actions">
          <button className="add-new-btn" onClick={onBack}>{t`Back`}</button>
          {!showPaymentForm && (
            <button className="add-new-btn" onClick={handleProceedToPayment}>
              {userRole === "ROLE_WORKER" || userRole === "ROLE_MANAGER"
                ? t`Create Reservation`
                : t`Proceed to Payment`}
            </button>
          )}
        </div>

        {showPaymentForm && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm
              paymentData={paymentData}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={setErrorMessage}
            />
          </Elements>
        )}

        {(successMessage || errorMessage) && (
          <Modal
            message={successMessage || errorMessage}
            onClose={() => {
              setSuccessMessage("");
              setErrorMessage("");
              onBack();
            }}
            isSuccess={!!successMessage}
          />
        )}
      </div>
    </div>
  );
};

export default SummaryModal;
